import { useEffect, useMemo, useState } from 'react';
import { escapeHtml, type ReportColumn } from './ReportColumnsModal';

interface GroupByModalProps {
  open: boolean;
  /** All columns the selected report produces. */
  columns: ReportColumn[];
  loading?: boolean;
  onClose: () => void;
  /** Called with the generated group-by card HTML to insert. */
  onInsert: (html: string) => void;
}

/** Builds the innermost table of the chosen display columns for a group's rows. */
function buildLeafTable(columns: ReportColumn[], indent: string): string {
  const headerCells = columns
    .map(
      (c) =>
        `${indent}      <th style="text-align:left;border-bottom:2px solid #dee2e6;padding:6px 8px;font-size:11px;">${escapeHtml(
          c.label,
        )}</th>`,
    )
    .join('\n');
  const bodyCells = columns
    .map(
      (c) =>
        `${indent}      <td style="border-bottom:1px solid #eee;padding:6px 8px;font-size:11px;">{{lookup this "${c.id}"}}</td>`,
    )
    .join('\n');

  return `${indent}<table style="width:100%;border-collapse:collapse;">
${indent}  <thead>
${indent}    <tr>
${headerCells}
${indent}    </tr>
${indent}  </thead>
${indent}  <tbody>
${indent}    {{#each rows}}
${indent}    <tr>
${bodyCells}
${indent}    </tr>
${indent}    {{/each}}
${indent}  </tbody>
${indent}</table>`;
}

/**
 * Builds a Handlebars "group-by cards" block. Supports one or more grouping
 * columns (nested cards). Each level renders a card with a header (group label +
 * value + count); the deepest level renders a table of the chosen columns.
 */
export function buildGroupByCards(groups: ReportColumn[], columns: ReportColumn[]): string {
  // Nesting depth tint for card headers.
  const shades = ['#f3f4ff', '#f9fafb', '#f5f3ff', '#f0fdf4'];

  const buildLevel = (level: number, indent: string): string => {
    const group = groups[level];
    const isLeaf = level === groups.length - 1;
    const bg = shades[level % shades.length];
    const inner = isLeaf
      ? buildLeafTable(columns, `${indent}  `)
      : `${indent}  {{#each groups}}\n${buildLevel(level + 1, `${indent}  `)}\n${indent}  {{/each}}`;

    return `${indent}<div style="margin:0 0 ${
      12 - level * 2 > 4 ? 12 - level * 2 : 4
    }px;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden;">
${indent}  <div style="background:${bg};padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;font-weight:600;color:#1f2937;">
${indent}    ${escapeHtml(group.label)}: {{key}} <span style="color:#6b7280;font-weight:400;">({{count}})</span>
${indent}  </div>
${indent}  <div style="padding:${isLeaf ? '0' : '10px'};">
${inner}
${indent}  </div>
${indent}</div>`;
  };

  const keys = groups.map((g) => `"${g.id}"`).join(' ');
  return `{{#groupBy data ${keys}}}
${buildLevel(0, '')}
{{/groupBy}}`;
}

/**
 * Modal to configure a "Group By" block: choose the column to group by and the
 * columns to show inside each group card.
 */
export function GroupByModal({
  open,
  columns,
  loading = false,
  onClose,
  onInsert,
}: GroupByModalProps) {
  // Ordered list of columns to group by (outer → inner nesting).
  const [groupBy, setGroupBy] = useState<ReportColumn[]>([]);
  const [selected, setSelected] = useState<ReportColumn[]>([]);

  // Default: no grouping, all columns shown.
  useEffect(() => {
    if (open) {
      setGroupBy([]);
      setSelected(columns);
    }
  }, [open, columns]);

  const order = useMemo(() => new Map(columns.map((c, i) => [c.id, i])), [columns]);
  const groupIds = useMemo(() => new Set(groupBy.map((g) => g.id)), [groupBy]);

  // Columns available to show inside the card exclude the group columns.
  const showableColumns = useMemo(
    () => columns.filter((c) => !groupIds.has(c.id)),
    [columns, groupIds],
  );
  const effectiveSelected = useMemo(
    () => selected.filter((c) => !groupIds.has(c.id)),
    [selected, groupIds],
  );
  const allSelected =
    showableColumns.length > 0 && effectiveSelected.length === showableColumns.length;

  if (!open) return null;

  const toggleGroup = (col: ReportColumn) => {
    setGroupBy((prev) =>
      prev.some((c) => c.id === col.id) ? prev.filter((c) => c.id !== col.id) : [...prev, col],
    );
  };

  const moveGroup = (index: number, dir: -1 | 1) => {
    setGroupBy((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const toggle = (col: ReportColumn) => {
    setSelected((prev) => {
      if (prev.some((c) => c.id === col.id)) return prev.filter((c) => c.id !== col.id);
      return [...prev, col].sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    });
  };

  const move = (index: number, dir: -1 | 1) => {
    setSelected((prev) => {
      // Reorder within the effective (shown) list, then merge back.
      const shown = prev.filter((c) => !groupIds.has(c.id));
      const target = index + dir;
      if (target < 0 || target >= shown.length) return prev;
      [shown[index], shown[target]] = [shown[target], shown[index]];
      return shown;
    });
  };

  const canInsert = groupBy.length > 0 && effectiveSelected.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Insert group-by block"
    >
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Group By</h3>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              Group the report data by a column. A card repeats for each group, showing the columns
              you choose.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
              Loading report columns…
            </div>
          ) : columns.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No columns found for this report.
            </p>
          ) : (
            <div className="space-y-5">
              {/* Group-by columns (multiple = nested cards) */}
              <div>
                <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Group by ({groupBy.length})
                </span>
                <p className="mb-2 text-xs text-gray-400 dark:text-gray-500">
                  Pick one or more columns. Multiple columns create nested groups (outer → inner);
                  use the arrows to set nesting order.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
                    {columns.map((col) => {
                      const checked = groupIds.has(col.id);
                      return (
                        <label
                          key={col.id}
                          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleGroup(col)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                          />
                          <span className="text-gray-700 dark:text-gray-300">{col.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div>
                    {groupBy.length === 0 ? (
                      <p className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                        No grouping selected.
                      </p>
                    ) : (
                      <ul className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
                        {groupBy.map((col, index) => (
                          <li
                            key={col.id}
                            className="flex items-center justify-between gap-2 rounded px-2 py-1 text-sm"
                          >
                            <span className="truncate text-gray-700 dark:text-gray-300">
                              {index + 1}. {col.label}
                            </span>
                            <span className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => moveGroup(index, -1)}
                                disabled={index === 0}
                                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 dark:hover:bg-gray-800"
                                aria-label={`Move ${col.label} up`}
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m4.5 15.75 7.5-7.5 7.5 7.5"
                                  />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => moveGroup(index, 1)}
                                disabled={index === groupBy.length - 1}
                                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 dark:hover:bg-gray-800"
                                aria-label={`Move ${col.label} down`}
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m19.5 8.25-7.5 7.5-7.5-7.5"
                                  />
                                </svg>
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Columns to show */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Columns to show ({showableColumns.length})
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelected(allSelected ? [] : columns)}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                    >
                      {allSelected ? 'Clear all' : 'Select all'}
                    </button>
                  </div>
                  <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
                    {showableColumns.map((col) => {
                      const checked = effectiveSelected.some((c) => c.id === col.id);
                      return (
                        <label
                          key={col.id}
                          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(col)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                          />
                          <span className="text-gray-700 dark:text-gray-300">{col.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Order ({effectiveSelected.length})
                  </span>
                  {effectiveSelected.length === 0 ? (
                    <p className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
                      Select at least one column to show.
                    </p>
                  ) : (
                    <ul className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2 dark:border-gray-700">
                      {effectiveSelected.map((col, index) => (
                        <li
                          key={col.id}
                          className="flex items-center justify-between gap-2 rounded px-2 py-1 text-sm"
                        >
                          <span className="truncate text-gray-700 dark:text-gray-300">
                            {index + 1}. {col.label}
                          </span>
                          <span className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => move(index, -1)}
                              disabled={index === 0}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 dark:hover:bg-gray-800"
                              aria-label={`Move ${col.label} up`}
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m4.5 15.75 7.5-7.5 7.5 7.5"
                                />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => move(index, 1)}
                              disabled={index === effectiveSelected.length - 1}
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 dark:hover:bg-gray-800"
                              aria-label={`Move ${col.label} down`}
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                                />
                              </svg>
                            </button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={() => onInsert(buildGroupByCards(groupBy, effectiveSelected))}
            disabled={!canInsert}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Insert group
          </button>
        </div>
      </div>
    </div>
  );
}
